/**
 * Rule builder - groups of tags (OR within group, AND between groups)
 * For Media Devoted | Created By Ciro Autiero
 */

import type { FC } from 'react';
import { Plus } from 'lucide-react';

import { Button } from '@/components/ui';
import { RuleBuilderGroup } from './RuleBuilderGroup';

export interface RuleBuilderProps {
  groups: string[][];
  availableTags: string[];
  tagColors?: Record<string, string>;
  onChange: (groups: string[][]) => void;
}

export const RuleBuilder: FC<RuleBuilderProps> = ({
  groups,
  availableTags,
  tagColors,
  onChange,
}) => {
  const updateGroup = (index: number, tags: string[]) => {
    const next = [...groups];
    next[index] = tags;
    onChange(next);
  };

  const removeGroup = (index: number) => {
    onChange(groups.filter((_, i) => i !== index));
  };

  const addGroup = () => {
    onChange([...groups, []]);
  };

  return (
    <div className="flex flex-col gap-4">
      <p className="text-xs leading-relaxed text-muted-foreground">
        <strong className="text-foreground">OR</strong> within a group · <strong className="text-foreground">AND</strong> between groups
      </p>
      <div className="flex flex-col gap-3">
        {groups.map((tags, i) => (
          <div key={i} className="flex flex-col gap-2">
            {i > 0 && (
              <div className="flex items-center gap-2">
                <div className="h-px flex-1 bg-border" />
                <span className="text-xs font-medium text-muted-foreground">and</span>
                <div className="h-px flex-1 bg-border" />
              </div>
            )}
            <RuleBuilderGroup
              groupIndex={i + 1}
              tags={tags}
              availableTags={availableTags}
              tagColors={tagColors}
              onTagsChange={(t) => updateGroup(i, t)}
              onRemove={() => removeGroup(i)}
              canRemove={groups.length > 1}
            />
          </div>
        ))}
      </div>
      <Button variant="outline" size="sm" className="w-fit gap-1.5" onClick={addGroup}>
        <Plus className="h-3.5 w-3.5" />
        Add group (AND)
      </Button>
    </div>
  );
};
